import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { apiDashboard, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import {
    AlertTriangle,
    CalendarClock,
    ClipboardList,
    RotateCw,
    PhoneCall,
    ArrowRight,
    Users,
    ClipboardCheck,
} from "lucide-react";
import { fmtTime, formatShortDate } from "@/lib/date";

export default function Home() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setData(await apiDashboard());
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const isClient = user?.role === "Client";
    const isConsultant = user?.role === "Consultant";
    const isManager =
        user?.role === "Super Admin" ||
        user?.role === "Admin" ||
        user?.role === "Team Lead";

    return (
        <Layout active="dashboard">
            <PageHeader
                title={`Hello, ${user?.full_name?.split(" ")[0] || "there"}`}
                subtitle={
                    isClient
                        ? "Coverage and recent handovers for your project."
                        : isConsultant
                          ? "Your upcoming shifts, handovers, and carry-forward."
                          : "Today's coverage, handover status, and carry-forward across your projects."
                }
                actions={null}
            />

            {loading ? (
                <Empty label="Loading dashboard…" />
            ) : !data ? (
                <Empty label="No data yet." />
            ) : data.empty ? (
                <Empty label="You are not authorised on any project yet. Ask an admin to grant access." />
            ) : (
                <div className="space-y-6" data-testid="dashboard">
                    <CoverageBlock coverage={data.coverage} today={data.today} isClient={isClient} />
                    {isConsultant && (
                        <ConsultantMyStuff data={data} />
                    )}
                    <RecentHandoversBlock rows={data.recent_handovers} isClient={isClient} />
                    {(isManager || isConsultant) && (
                        <CarryForwardBlock groups={data.carry_forward_by_project} />
                    )}
                </div>
            )}
        </Layout>
    );
}

function Empty({ label }) {
    return (
        <div
            data-testid="dashboard-empty"
            className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted"
        >
            {label}
        </div>
    );
}

function Card({ title, count, icon: Icon, children, action, testId }) {
    return (
        <section
            data-testid={testId}
            className="bg-white border border-ntt-border rounded-xl p-5"
        >
            <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-[15px] font-semibold text-[#070F26] flex items-center gap-2">
                    {Icon && <Icon className="h-4 w-4 text-[#0072BC]" />}
                    {title}
                    {count !== undefined && (
                        <span className="text-[11px] font-medium text-ntt-muted bg-[#F4F6F9] rounded-full h-5 px-2 inline-flex items-center">
                            {count}
                        </span>
                    )}
                </h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function CoverageBlock({ coverage, today, isClient }) {
    const totalGaps = coverage.reduce((s, c) => s + (c.gap_count || 0), 0);
    return (
        <Card
            testId="coverage-card"
            title={`Current coverage · ${formatShortDate(today)}`}
            icon={CalendarClock}
            action={
                totalGaps > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                        <AlertTriangle className="h-3 w-3" />
                        {totalGaps} Gap{totalGaps === 1 ? "" : "s"}
                    </span>
                ) : null
            }
        >
            {coverage.length === 0 ? (
                <div className="text-[13px] text-ntt-muted italic">
                    No active projects with shifts to show.
                </div>
            ) : (
                <div className="space-y-4">
                    {coverage.map((c) => (
                        <div key={c.project_id} data-testid={`coverage-${c.project_id}`}>
                            <div className="flex items-baseline justify-between mb-2">
                                <div className="text-[13px] font-medium text-[#070F26]">
                                    {c.project_name}{" "}
                                    <span className="text-ntt-muted font-mono text-[11px]">
                                        · {c.project_code}
                                    </span>
                                </div>
                                {c.gap_count > 0 && (
                                    <span className="text-[11px] text-red-700">
                                        {c.gap_count} Gap{c.gap_count === 1 ? "" : "s"}
                                    </span>
                                )}
                            </div>
                            {c.shifts.length === 0 ? (
                                <div className="text-[12px] text-ntt-muted italic">
                                    No shifts defined.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {c.shifts.map((s) => (
                                        <div
                                            key={s.shift_id}
                                            data-testid={`coverage-shift-${s.shift_id}`}
                                            className={`rounded-md border p-3 ${s.is_gap ? "border-red-200 bg-[#FEF6F6]" : "border-ntt-border bg-white"}`}
                                        >
                                            <div className="text-[12px] font-medium text-[#070F26]">
                                                {s.shift_name}
                                            </div>
                                            <div className="text-[10px] text-ntt-muted font-mono">
                                                {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                                            </div>
                                            {s.is_gap ? (
                                                <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-700">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Uncovered
                                                </div>
                                            ) : (
                                                <div className="mt-1.5 space-y-0.5">
                                                    {s.assignments.slice(0, 3).map((a) => (
                                                        <div key={a.assignment_id} className="text-[11px] text-[#2B2F3A] truncate flex items-center gap-1">
                                                            {a.user_full_name || "Assigned"}
                                                            {a.on_call && (
                                                                <PhoneCall className="h-3 w-3 text-[#8a5a00]" />
                                                            )}
                                                        </div>
                                                    ))}
                                                    {s.assignments.length > 3 && (
                                                        <div className="text-[10px] text-ntt-muted">
                                                            +{s.assignments.length - 3} more
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {!isClient && (
                <div className="mt-4 pt-3 border-t border-ntt-border">
                    <Link to="/roster" className="text-[12px] font-medium text-[#0072BC] inline-flex items-center gap-1 hover:underline" data-testid="coverage-open-roster">
                        Open roster
                        <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
            )}
        </Card>
    );
}

function RecentHandoversBlock({ rows, isClient }) {
    const overdue = rows.filter((r) => r.overdue_ack).length;
    return (
        <Card
            testId="recent-handovers-card"
            title="Handovers · Last 24 Hours"
            icon={ClipboardCheck}
            count={rows.length}
            action={
                overdue > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                        <AlertTriangle className="h-3 w-3" />
                        {overdue} overdue ack
                    </span>
                ) : null
            }
        >
            {rows.length === 0 ? (
                <div className="text-[13px] text-ntt-muted italic" data-testid="recent-empty">
                    Nothing submitted in the last 24 hours.
                </div>
            ) : (
                <div className="space-y-1.5">
                    {rows.map((h) => (
                        <Link
                            key={h.handover_id}
                            to={`/handovers/${h.handover_id}`}
                            data-testid={`recent-${h.handover_id}`}
                            className="flex items-center gap-3 p-2 rounded-md border border-ntt-border hover:bg-[#FBFCFE]"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium text-[#070F26] truncate">
                                    {h.shift_name} · {formatShortDate(h.handover_date)}
                                </div>
                                <div className="text-[11px] text-ntt-muted truncate">
                                    {h.project_code} ·{" "}
                                    {h.outgoing_user_name || "—"} →{" "}
                                    {h.incoming_user_name || "(open)"}
                                </div>
                            </div>
                            {h.overdue_ack && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                    overdue
                                </span>
                            )}
                            <StatusPill
                                label={h.status}
                                tone={h.status === "Acknowledged" ? "green" : "blue"}
                            />
                        </Link>
                    ))}
                </div>
            )}
        </Card>
    );
}

function CarryForwardBlock({ groups }) {
    const total = groups.reduce((s, g) => s + g.count, 0);
    return (
        <Card
            testId="carry-forward-card"
            title="Open Carry-Forward Items"
            icon={RotateCw}
            count={total}
        >
            {groups.length === 0 ? (
                <div className="text-[13px] text-ntt-muted italic" data-testid="cf-empty">
                    Nothing carrying forward. Clean board.
                </div>
            ) : (
                <div className="space-y-3">
                    {groups.map((g) => (
                        <div
                            key={g.project_id}
                            data-testid={`cf-project-${g.project_id}`}
                            className="border border-ntt-border rounded-md p-3"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[13px] font-medium text-[#070F26]">
                                    {g.project_name}{" "}
                                    <span className="text-ntt-muted text-[11px] font-mono">
                                        · {g.project_code}
                                    </span>
                                </div>
                                <span className="text-[11px] font-medium text-ntt-muted bg-[#F4F6F9] rounded px-2 py-0.5">
                                    {g.count} open
                                </span>
                            </div>
                            {g.oldest && (
                                <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-2 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3 shrink-0" />
                                    <span className="truncate">
                                        Oldest: <span className="font-medium">{g.oldest.title}</span>
                                        {g.oldest.reference ? ` · ${g.oldest.reference}` : ""}
                                    </span>
                                </div>
                            )}
                            <div className="space-y-0.5">
                                {g.items.slice(0, 5).map((it) => (
                                    <div key={it.item_id} className="text-[12px] text-[#2B2F3A] truncate flex items-center gap-1.5">
                                        <span className="text-ntt-muted text-[10px] font-mono">
                                            {it.category}
                                        </span>
                                        <span className="font-medium">{it.title}</span>
                                        {it.reference && (
                                            <span className="text-ntt-muted">· {it.reference}</span>
                                        )}
                                    </div>
                                ))}
                                {g.count > 5 && (
                                    <div className="text-[11px] text-ntt-muted">
                                        +{g.count - 5} more
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

function ConsultantMyStuff({ data }) {
    const upcoming = data.my_upcoming_shifts || [];
    const todo = data.my_todo_handovers || [];
    return (
        <>
            <Card testId="my-shifts-card" title="My Upcoming Shifts" icon={CalendarClock} count={upcoming.length}>
                {upcoming.length === 0 ? (
                    <div className="text-[13px] text-ntt-muted italic">
                        No shifts scheduled in the next 7 days.
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {upcoming.map((a) => (
                            <div key={a.assignment_id} data-testid={`my-shift-${a.assignment_id}`} className="flex items-center gap-3 p-2 border border-ntt-border rounded-md bg-white">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium text-[#070F26]">
                                        {a.date}
                                    </div>
                                    <div className="text-[11px] text-ntt-muted truncate">
                                        Shift {a.shift_id} · {a.note || "no note"}
                                    </div>
                                </div>
                                {a.on_call && (
                                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded bg-[#FFC303] text-[#070F26] text-[11px] font-medium">
                                        <PhoneCall className="h-3 w-3" />
                                        on call
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>
            <Card testId="my-todo-card" title="Handovers To Complete or Acknowledge" icon={ClipboardList} count={todo.length}>
                {todo.length === 0 ? (
                    <div className="text-[13px] text-ntt-muted italic">
                        Nothing pending. You're clear.
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {todo.map((h) => (
                            <Link
                                key={h.handover_id}
                                to={`/handovers/${h.handover_id}`}
                                data-testid={`my-todo-${h.handover_id}`}
                                className="flex items-center gap-3 p-2 rounded-md border border-ntt-border hover:bg-[#FBFCFE]"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium text-[#070F26] truncate">
                                        {h.shift_name} · {formatShortDate(h.handover_date)} · {h.project_code}
                                    </div>
                                    <div className="text-[11px] text-ntt-muted">
                                        {h.status === "Draft" ? "Complete + submit" : "Acknowledge"}
                                    </div>
                                </div>
                                <StatusPill label={h.status} tone={h.status === "Draft" ? "gray" : "blue"} />
                            </Link>
                        ))}
                    </div>
                )}
            </Card>
        </>
    );
}
